/**
 * KfmlWorklistToggle — Steuert projects.kfml_worklist (PV-016).
 *
 * Zwei Darstellungen (Prop `variant`, dieselbe Mutation):
 *   - `checkbox` (default): Checkbox „KFML-Worklist" für die Übergabe-Ansicht, direkt neben
 *     „Wieder fällig am" (KlFollowupDateEditor). Setzt UND entfernt (`value` = Ist-Zustand).
 *   - `remove`: expliziter „Von der Worklist entfernen"-Button für die Worklist-Karte selbst.
 *     Dort ist der Haken immer gesetzt und die einzige sinnvolle Aktion ist das Entfernen —
 *     eine immer-angehakte Checkbox wäre dort missverständlich (#40). Der Button ist der
 *     einzige Weg, einen Haken zu entfernen, wenn das Projekt nicht (mehr) in der Übergabe
 *     erscheint (Phase ≠ Neu/Handover oder abgeschlossen).
 *
 * ABGRENZUNG: Das ist die KFML-WORKLIST (manueller Haken, persistent, steuert die
 * Worklist-Ansicht) — NICHT die KFML-Todoliste (LLM-Extraktion aus Intake-Notizen,
 * Issue #14, Button „KFML-Todos für KuSe erstellen" in derselben Ansicht).
 *
 * Setzen/Entfernen gehen über zwei eigene POST-Routen (nicht PATCH), weil beide einen
 * project_history-Eintrag erzeugen und gegen Doppel-Setzen mit 409 schützen.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { useToast } from '../toast.js';

export function KfmlWorklistToggle({
  projectId,
  value,
  canEdit,
  onSaved,
  variant = 'checkbox',
  wartetAufSetup = false,
}: {
  projectId: string;
  value: boolean;
  canEdit: boolean;
  onSaved?: () => void;
  variant?: 'checkbox' | 'remove';
  /**
   * #136: Projekt steht noch in der Eingangsphase. Die Worklist zeigt es erst ab Setup —
   * ein gesetzter Haken bekommt deshalb den Hinweis „ab Setup", damit „angehakt, aber nicht
   * in der Liste" nicht wie ein Fehler aussieht.
   */
  wartetAufSetup?: boolean;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const mut = useMutation({
    mutationFn: (next: boolean) =>
      next ? api.setKfmlWorklist(projectId) : api.clearKfmlWorklist(projectId),
    onSuccess: (_data, next) => {
      void queryClient.invalidateQueries({ queryKey: ['v2-kfml-worklist'] });
      void queryClient.invalidateQueries({ queryKey: ['v2-handover-projects'] });
      void queryClient.invalidateQueries({ queryKey: ['v2-project', projectId] });
      toast.success(
        next
          ? t('v2.worklist.toast_set', 'Auf die KFML-Worklist gesetzt')
          : t('v2.worklist.toast_cleared', 'Von der KFML-Worklist entfernt'),
      );
      onSaved?.();
    },
    onError: (err) =>
      toast.error(
        t('v2.worklist.toast_failed', 'Speichern fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  if (variant === 'remove') {
    return (
      <button
        type="button"
        onClick={() => mut.mutate(false)}
        disabled={!canEdit || mut.isPending}
        title={canEdit ? undefined : t('v2.reader.tooltip', 'Sie haben Leserechte')}
        className="text-xs px-3 py-1.5 rounded-md"
        style={{
          background: 'var(--surface-2)',
          color: 'var(--ink-2)',
          border: '1px solid var(--line-soft)',
          cursor: canEdit && !mut.isPending ? 'pointer' : 'not-allowed',
          opacity: canEdit && !mut.isPending ? 1 : 0.6,
        }}
      >
        {t('v2.worklist.remove_button', 'Von der Worklist entfernen')}
      </button>
    );
  }

  return (
    <label
      className="flex items-center gap-1.5 text-xs"
      style={{
        color: 'var(--ink-3)',
        cursor: canEdit && !mut.isPending ? 'pointer' : 'not-allowed',
        opacity: canEdit && !mut.isPending ? 1 : 0.6,
      }}
      title={
        canEdit
          ? t('v2.worklist.toggle_hint', 'Projekt auf die Arbeitsliste der Kaufm. Leitung setzen')
          : t('v2.reader.tooltip', 'Sie haben Leserechte')
      }
    >
      <input
        type="checkbox"
        checked={value}
        disabled={!canEdit || mut.isPending}
        onChange={(e) => mut.mutate(e.target.checked)}
      />
      <span>{t('v2.worklist.toggle_label', 'KFML-Worklist')}</span>
      {value && wartetAufSetup ? (
        <span
          data-testid="kfml-worklist-ab-setup"
          title={t(
            'v2.worklist.ab_setup_hint',
            'Erscheint in der KFML-Worklist, sobald das Projekt auf Setup verschoben ist.',
          )}
          style={{ color: 'var(--ink-4)', fontStyle: 'italic' }}
        >
          {t('v2.worklist.ab_setup', '· ab Setup')}
        </span>
      ) : null}
    </label>
  );
}
